'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Edit, 
  Save, 
  UserX, 
  Shield, 
  Bell,
  Eye,
  EyeOff,
  AlertTriangle,
  ArrowLeft,
  Users,
  FileText,
  Upload,
  CheckCircle,
  Clock,
  XCircle,
  BarChart3
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Input } from '@myapp/ui/components/input';
import { useAuth } from '@/components/auth/MockAuthProvider';

// 각 사용자별 상세 프로필 데이터 (Google OAuth 계정들과 연동)
const getUserProfileData = (userId: string, email: string) => {
  const profiles: { [key: string]: any } = {
    // Google OAuth 계정들 (이메일 기반 매칭)
    'chulsoo.kim@gmail.com': { // 김철수
      phone: '010-1234-5678',
      address: '강원도 춘천시 중앙로 123',
      joinDate: '2024.02.15',
      type: 'customer',
      orders: 32,
      reviews: 14,
      points: 8900,
    },
    'younghee.lee@gmail.com': { // 이영희
      phone: '010-2345-6789',
      address: '강원도 원주시 시청로 456',
      joinDate: '2024.03.10',
      type: 'customer',
      orders: 18,
      reviews: 8,
      points: 4200,
    },
    'minsoo.park@gmail.com': { // 박민수  
      phone: '010-3456-7890',
      address: '강원도 강릉시 해안로 789',
      joinDate: '2024.03.22',
      type: 'customer',
      orders: 25,
      reviews: 12,
      points: 6750,
    },
    'jiyeon.choi@gmail.com': { // 최지연
      phone: '010-4567-8901',
      address: '강원도 동해시 바다로 321',
      joinDate: '2024.04.05',
      type: 'customer',
      orders: 12,
      reviews: 6,
      points: 3400,
    },
    'admin@foodfit.kr': { // 강원푸드핏 관리자
      phone: '010-9999-0000',
      address: '강원도 춘천시 본사로 123',
      joinDate: '2023.12.01',
      type: 'admin',
      orders: 0,
      reviews: 0,
      points: 999999,
    },
    'farmer@gangwon.kr': { // 농업인
      phone: '010-1111-2222',
      address: '강원도 홍천군 농장로 456',
      joinDate: '2024.01.01',
      type: 'farmer',
      orders: 78,
      reviews: 35,
      points: 42500,
      farmerAccount: {
        status: 'approved',
        farmName: '홍천 유기농 농장',
        farmAddress: '강원도 홍천군 농장로 456',
        farmSize: '5헥타르',
        mainCrops: ['배추', '무', '당근', '감자', '옥수수'],
        certificationNumber: '123-45-67890',
        applicationDate: '2024.01.01',
        approvalDate: '2024.01.03',
        rejectionReason: ''
      }
    },
    // 기존 계정들 (하위 호환성 유지)
    '3': { // 박평창 (일반 고객)
      phone: '010-3456-7890',
      address: '강원도 평창군 대관령면 올림픽로 789',
      joinDate: '2024.03.10',
      type: 'customer',
      orders: 15,
      reviews: 7,
      points: 3200,
    },
    '4': { // 정농민 (농업인)
      phone: '010-4567-8901',
      address: '강원도 평창군 대관령면 농장길 456',
      joinDate: '2024.02.01',
      type: 'farmer',
      orders: 67,
      reviews: 34,
      points: 22100,
      farmerAccount: {
        status: 'approved',
        farmName: '평창 고랭지 농장',
        farmAddress: '강원도 평창군 대관령면 농장길 456',
        farmSize: '3헥타르',
        mainCrops: ['고랭지 배추', '감자', '당근'],
        certificationNumber: '234-56-78901',
        applicationDate: '2024.02.01',
        approvalDate: '2024.02.03',
        rejectionReason: ''
      }
    }
  };

  // 먼저 이메일로 매칭 시도
  if (email && profiles[email]) {
    return profiles[email];
  }
  
  // 이메일 매칭이 안되면 userId로 매칭
  if (profiles[userId]) {
    return profiles[userId];
  }
  
  // 기본값 반환
  return {
    phone: '010-0000-0000',
    address: '강원도',
    joinDate: '2024.01.01',
    type: 'customer',
    orders: 0,
    reviews: 0,
    points: 0,
  };
};

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showFarmerApplication, setShowFarmerApplication] = useState(false);
  const [farmerApplicationData, setFarmerApplicationData] = useState({
    farmName: '',
    farmAddress: '',
    farmSize: '',
    mainCrops: '',
    certificationNumber: ''
  });
  const [notifications, setNotifications] = useState({
    orderUpdates: true,
    promotions: false,
    newsletter: true
  });

  // 로그인 안 된 경우 처리
  if (!authUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="text-center py-12">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">로그인이 필요합니다</h2>
            <p className="text-gray-600 mb-4">프로필을 확인하려면 로그인해주세요.</p>
            <Button>로그인하기</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 현재 로그인된 사용자의 프로필 데이터 가져오기
  const profileData = getUserProfileData(authUser.id, authUser.email);
  const currentUser = {
    ...authUser,
    ...profileData
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleDeleteAccount = () => {
    if (deletePassword === 'delete') {
      alert('계정이 삭제되었습니다.');
      setShowDeleteConfirm(false);
      setDeletePassword('');
    } else {
      alert('잘못된 확인 텍스트입니다.');
    }
  };

  const handleFarmerApplication = () => {
    const newUser = {
      ...currentUser,
      farmerAccount: {
        status: 'pending',
        farmName: farmerApplicationData.farmName,
        farmAddress: farmerApplicationData.farmAddress,
        farmSize: farmerApplicationData.farmSize,
        mainCrops: farmerApplicationData.mainCrops.split(',').map(crop => crop.trim()),
        certificationNumber: farmerApplicationData.certificationNumber,
        applicationDate: new Date().toISOString().split('T')[0],
        approvalDate: null,
        rejectionReason: ''
      }
    };
    
    alert('농업인 계정 신청이 완료되었습니다. 검토 후 연락드리겠습니다.');
    setShowFarmerApplication(false);
    setFarmerApplicationData({
      farmName: '',
      farmAddress: '',
      farmSize: '',
      mainCrops: '',
      certificationNumber: ''
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              돌아가기
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">프로필 설정</h1>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Profile Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    기본 정보
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? (
                      <>
                        <Save className="w-4 h-4 mr-1" />
                        저장
                      </>
                    ) : (
                      <>
                        <Edit className="w-4 h-4 mr-1" />
                        수정
                      </>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center text-2xl">
                    {currentUser.avatar}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{currentUser.name}</h3>
                    <p className="text-gray-600">{currentUser.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={currentUser.type === 'farmer' ? 'default' : 'secondary'}>
                        {currentUser.type === 'farmer' ? '🌾 농업인' : '👤 일반회원'}
                      </Badge>
                      <span className="text-sm text-gray-500">가입일: {currentUser.joinDate}</span>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      <Phone className="w-4 h-4 inline mr-1" />
                      전화번호
                    </label>
                    {isEditing ? (
                      <Input
                        type="tel"
                        value={currentUser.phone}
                        onChange={(e) => {}}
                      />
                    ) : (
                      <p className="text-gray-900">{currentUser.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      <Mail className="w-4 h-4 inline mr-1" />
                      이메일
                    </label>
                    <p className="text-gray-500">{currentUser.email} (변경불가)</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    주소
                  </label>
                  {isEditing ? (
                    <Input
                      type="text"
                      value={currentUser.address}
                      onChange={(e) => {}}
                    />
                  ) : (
                    <p className="text-gray-900">{currentUser.address}</p>
                  )}
                </div>

                {isEditing && (
                  <div className="pt-4">
                    <Button onClick={handleSave} className="w-full">
                      <Save className="w-4 h-4 mr-2" />
                      변경사항 저장
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Farmer Account Section */}
            {currentUser.farmerAccount?.status === 'approved' ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      농업인 계정 정보
                    </span>
                    <div className="flex gap-2">
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        승인완료
                      </Badge>
                      <Link href="/farmer-dashboard">
                        <Button size="sm">
                          <BarChart3 className="w-4 h-4 mr-1" />
                          대시보드
                        </Button>
                      </Link>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-700">농장명</label>
                      <p className="text-gray-900">{currentUser.farmerAccount.farmName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">농장 규모</label>
                      <p className="text-gray-900">{currentUser.farmerAccount.farmSize}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">농장 주소</label>
                      <p className="text-gray-900">{currentUser.farmerAccount.farmAddress}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">주요 작물</label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {currentUser.farmerAccount.mainCrops.map((crop, index) => (
                          <Badge key={index} variant="outline">{crop}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">인증번호</label>
                      <p className="text-gray-900">{currentUser.farmerAccount.certificationNumber}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : currentUser.farmerAccount?.status === 'pending' ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    농업인 계정 심사중
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">심사 진행중</h3>
                    <p className="text-gray-600 mb-4">
                      농업인 계정 신청이 접수되었습니다.<br />
                      심사 완료까지 1-2일 소요될 예정입니다.
                    </p>
                    <p className="text-sm text-gray-500">
                      신청일: {currentUser.farmerAccount.applicationDate}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : currentUser.farmerAccount?.status === 'rejected' ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    농업인 계정 신청
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">신청 반려</h3>
                    <p className="text-gray-600 mb-4">
                      농업인 계정 신청이 반려되었습니다.
                    </p>
                    <div className="text-sm text-gray-500 mb-4">
                      반려 사유: {currentUser.farmerAccount.rejectionReason}
                    </div>
                    <Button onClick={() => setShowFarmerApplication(true)}>
                      재신청하기
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    농업인 계정 신청
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">농업인 계정 신청</h3>
                    <p className="text-gray-600 mb-4">
                      강원도 농산물을 직접 판매하고<br />
                      농가 스토리를 공유해보세요!
                    </p>
                    <Button onClick={() => setShowFarmerApplication(true)}>
                      <Users className="w-4 h-4 mr-2" />
                      농업인 계정 신청하기
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notification Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  알림 설정
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(notifications).map(([key, enabled]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">
                        {key === 'orderUpdates' ? '주문 업데이트' :
                         key === 'promotions' ? '프로모션 알림' : '뉴스레터'}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {key === 'orderUpdates' ? '주문 상태 변경 시 알림을 받습니다' :
                         key === 'promotions' ? '할인 및 이벤트 정보를 받습니다' : '농산물 소식을 받습니다'}
                      </p>
                    </div>
                    <Button
                      variant={enabled ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNotifications(prev => ({ ...prev, [key]: !enabled }))}
                    >
                      {enabled ? 'ON' : 'OFF'}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  계정 삭제
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  계정을 삭제하면 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.
                </p>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <UserX className="w-4 h-4 mr-2" />
                  계정 삭제
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats */}
          <div className="space-y-6">
            {/* Activity Stats */}
            <Card>
              <CardHeader>
                <CardTitle>활동 통계</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{currentUser.orders}</div>
                  <div className="text-sm text-gray-600">총 주문</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{currentUser.reviews}</div>
                  <div className="text-sm text-gray-600">작성 리뷰</div>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{currentUser.points.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">적립 포인트</div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>바로가기</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link href="/orders">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    주문 내역
                  </Button>
                </Link>
                <Link href="/reviews">
                  <Button variant="outline" className="w-full justify-start">
                    <Users className="w-4 h-4 mr-2" />
                    리뷰 관리
                  </Button>
                </Link>
                <Link href="/bookmarks">
                  <Button variant="outline" className="w-full justify-start">
                    <Shield className="w-4 h-4 mr-2" />
                    즐겨찾기
                  </Button>
                </Link>
                {currentUser.farmerAccount?.status === 'approved' && (
                  <Link href="/farmer-dashboard">
                    <Button className="w-full justify-start">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      농업인 대시보드
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Farmer Application Modal */}
        {showFarmerApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>농업인 계정 신청</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">농장명</label>
                  <Input
                    value={farmerApplicationData.farmName}
                    onChange={(e) => setFarmerApplicationData(prev => ({
                      ...prev,
                      farmName: e.target.value
                    }))}
                    placeholder="예: 춘천농장"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">농장 주소</label>
                  <Input
                    value={farmerApplicationData.farmAddress}
                    onChange={(e) => setFarmerApplicationData(prev => ({
                      ...prev,
                      farmAddress: e.target.value
                    }))}
                    placeholder="예: 강원도 춘천시..."
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">농장 규모</label>
                  <Input
                    value={farmerApplicationData.farmSize}
                    onChange={(e) => setFarmerApplicationData(prev => ({
                      ...prev,
                      farmSize: e.target.value
                    }))}
                    placeholder="예: 2헥타르"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">주요 작물 (콤마로 구분)</label>
                  <Input
                    value={farmerApplicationData.mainCrops}
                    onChange={(e) => setFarmerApplicationData(prev => ({
                      ...prev,
                      mainCrops: e.target.value
                    }))}
                    placeholder="예: 배추, 감자, 옥수수"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">사업자등록번호 또는 농업경영체번호</label>
                  <Input
                    value={farmerApplicationData.certificationNumber}
                    onChange={(e) => setFarmerApplicationData(prev => ({
                      ...prev,
                      certificationNumber: e.target.value
                    }))}
                    placeholder="예: 123-45-67890"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowFarmerApplication(false)}
                    className="flex-1"
                  >
                    취소
                  </Button>
                  <Button
                    onClick={handleFarmerApplication}
                    className="flex-1"
                    disabled={!farmerApplicationData.farmName || !farmerApplicationData.farmAddress}
                  >
                    신청하기
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-red-600">계정 삭제 확인</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-red-800 text-sm">
                    ⚠️ 이 작업은 되돌릴 수 없습니다. 계정을 삭제하면 모든 데이터가 영구적으로 삭제됩니다.
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium">확인을 위해 "delete"를 입력하세요</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="delete"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                    }}
                    className="flex-1"
                  >
                    취소
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    className="flex-1"
                    disabled={deletePassword !== 'delete'}
                  >
                    삭제
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}